/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useState } from "react";
import StackedBarChart from "components/ReportsPaymentMethods/StackeBarChart";
import BarChart from "components/ReportsPaymentMethods/BarChart";
import List from "components/common/ListWidget";
import moment from "moment";
import { useBusinessContext } from "BusinessApp";
import {
  filterSortMapSellsByHourSameDay,
  filterSortMapSellsByDay,
  filterSortMapSellsByMonth,
  getDateHeader,
} from "helpers/dataHelper";
import { utils, writeFile } from "xlsx";
import { toPng } from "html-to-image";
import {
  useOrderPaymentDailySummary,
  useOrderPaymentHourlySummary,
  useOrderPaymentMonthlySummary,
} from "graphql/useOrderPaymentSummary";
import { useOrderPaymentSummary } from "graphql/useOrderSummary";
import { joinPaymentMethodValues } from "containers/ReportsPaymentMethods/helpers";
import { useTranslation } from "react-i18next";
import TopMenu from "components/Reports/TopMenu";
import DocumentPDF from "./DocumentPDF";
import s from "./styles";
import { printDocumentPDF } from "helpers/utils";

function ReportsPaymentMethods() {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState({ columnId: "earnings", order: 1 });
  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });

  const firstGraph = React.useRef(null);
  const secondGraph = React.useRef(null);

  const { storeId, currencyFormat } = useBusinessContext();

  const { orderPaymentSummary, loading: loading1 } = useOrderPaymentSummary({
    storeId,
    type: "fac",
    start: date.range.start.toDate(),
    end: date.range.end.toDate(),
  });

  const { orderPaymentSummary: orderReturnPaymentSummary, loading: loading2 } =
    useOrderPaymentSummary({
      storeId,
      type: "cre",
      start: date.range.start.toDate(),
      end: date.range.end.toDate(),
    });

  const { dataOrder } = useMemo(() => {
    const mapRefundCodeToOriginal = (code) => {
      const refundMapping = {
        refund_cash: "cash",
        refund_card: "card",
        refund_other: "other",
        store_credit_create: "store_credit_create",
        store_credit_apply: "store_credit_create",
      };
      return refundMapping[code] || code;
    };
    const combineSummaries = (summaries) => {
      const combined = summaries.reduce((acc, summary) => {
        const originalCode = mapRefundCodeToOriginal(summary._id.code);
        const key = originalCode;
        if (!acc[key]) {
          acc[key] = {
            ...summary,
            total: parseFloat(summary.total),
            count: summary.count,
            code: originalCode,
            _id: {
              ...summary._id,
              description: summary._id.description,
            },
          };
        } else {
          acc[key].total += parseFloat(summary.total);
          acc[key].count += summary.count;
        }
        return acc;
      }, {});
      return Object.values(combined);
    };

    const combinedOrderPaymentSummary = combineSummaries(orderPaymentSummary);
    const combinedOrderReturnPaymentSummary = combineSummaries(
      orderReturnPaymentSummary
    );

    const allSummaries = combineSummaries([
      ...combinedOrderPaymentSummary,
      ...combinedOrderReturnPaymentSummary,
    ]);

    const dataOrder = allSummaries
      .map((o) => {
        return joinPaymentMethodValues({
          orderPaymentSummary: combinedOrderPaymentSummary,
          orderReturnPaymentSummary: combinedOrderReturnPaymentSummary,
          name: o._id.description,
          method: o._id.code,
          methodReturn:
            o._id.code === "store_credit_create"
              ? o._id.code
              : o._id.code === "store_credit_apply"
              ? "store_credit_create"
              : `refund_${o.code}`,
        });
      })
      .sort((a, b) => (b.earnings - a.earnings) * sorting.order);

    const card =
      dataOrder.find(
        (o) => o.name === t("containers.ReportsPaymentMethods.card")
      ) || 0;
    const cash =
      dataOrder.find(
        (o) =>
          o.name === t("containers.ReportsPaymentMethods.cash") ||
          o.code === "cash"
      ) || 0;
    const other =
      dataOrder.find(
        (o) => o.name === t("containers.ReportsPaymentMethods.other")
      ) || 0;
    const storeCredit =
      dataOrder.find(
        (o) => o.name === t("containers.ReportsPaymentMethods.storeCreditUsage")
      ) || 0;

    return {
      dataOrder,
      card,
      cash,
      other,
      storeCredit,
    };
  }, [orderPaymentSummary, orderReturnPaymentSummary, sorting]);

  const returnData = useMemo(() => {
    const returnData = [];

    orderReturnPaymentSummary.forEach((o) => {
      if (o._id.code === "refund_card") {
        returnData.push({
          code: o._id.code,
          name: t("containers.ReportsPaymentMethods.card"),
          value: o.total,
        });
      } else if (o._id.code === "refund_cash") {
        returnData.push({
          code: o._id.code,
          name: t("containers.ReportsPaymentMethods.cash"),
          value: o.total,
        });
      } else if (o._id.code === "refund_other" || o._id.code === "other") {
        returnData.push({
          code: "other",
          name: t("containers.ReportsPaymentMethods.others"),
          value: o.total,
        });
      } else if (o._id.code === "store_credit_create") {
        returnData.push({
          code: o._id.code,
          name: t("containers.ReportsPaymentMethods.storeCreditUsage"),
          value: o.total,
        });
      }
    });
    return returnData;
  }, [orderReturnPaymentSummary]);

  const paymentMethods = dataOrder.map((v) => {
    const data = returnData.find((o) => o.code === v.code || o.name === v.name);
    return {
      method: t(`containers.ReportsPaymentMethods.${v.code}`),
      sells: v.sells,
      returns: parseFloat(data?.value || 0),
      operations: `${v.operationsPlus} / (${v.returns})`,
      earnings: v.earnings,
    };
  });

  const tableValues = dataOrder.map((v) => {
    const data = returnData.find((o) => {
      if (v.code === "store_credit_create" || v.code === "store_credit_apply") {
        return o.code === "store_credit_create";
      }

      return o.code === v.code || o.name === v.name;
    });
    return [
      t(`containers.ReportsPaymentMethods.${v.code}`),
      `${currencyFormat(v.sells)}`,
      `${currencyFormat(data?.value)}`,
      `${v.operationsPlus} / (${v.returns})`,
      `${currencyFormat(v.earnings)}`,
    ];
  });

  const isPeriodDay = date.periodType === "date";
  const isPeriodWeekOrMonth =
    date.periodType === "week" || date.periodType === "month";
  const isPeriodYear = date.periodType === "year";

  const { orderPaymentHourlySummary, loading: loading3 } =
    useOrderPaymentHourlySummary({
      storeId,
      start: date.range.start.toDate(),
      end: date.range.end.toDate(),
      type: "fac",
    });

  const { orderPaymentDailySummary, loading: loading4 } =
    useOrderPaymentDailySummary({
      storeId,
      start: date.range.start.toDate(),
      end: date.range.end.toDate(),
      type: "fac",
    });

  const { orderPaymentMonthlySummary, loading: loading5 } =
    useOrderPaymentMonthlySummary({
      storeId,
      start: date.range.start.toDate(),
      end: date.range.end.toDate(),
      type: "fac",
    });

  const { barChartTitle, cardData, cashData, otherData } = useMemo(() => {
    let barChartTitle = t("containers.ReportsPaymentMethods.paymentsByHour");
    if (isPeriodYear) {
      barChartTitle = t("containers.ReportsPaymentMethods.paymentsByMonth");
    } else if (date.periodType === "week") {
      barChartTitle = t("containers.ReportsPaymentMethods.paymentsByDay");
    } else if (date.periodType === "month") {
      barChartTitle = t("containers.ReportsPaymentMethods.paymentsByDay");
    }

    let cardData = [];
    let cashData = [];
    let otherData = [];

    if (isPeriodDay) {
      cardData = filterSortMapSellsByHourSameDay(
        orderPaymentHourlySummary.filter((v) => v._id.code === "card")
      );
      cashData = filterSortMapSellsByHourSameDay(
        orderPaymentHourlySummary.filter((v) => v._id.code === "cash")
      );
      otherData = filterSortMapSellsByHourSameDay(
        orderPaymentHourlySummary.filter(
          (v) => v._id.code != "card" && v._id.code != "cash"
        )
      );
    } else if (isPeriodWeekOrMonth) {
      cardData = filterSortMapSellsByDay(
        orderPaymentDailySummary.filter((v) => v._id.code === "card"),
        date.range.start,
        date.range.end
      );
      cashData = filterSortMapSellsByDay(
        orderPaymentDailySummary.filter((v) => v._id.code === "cash"),
        date.range.start,
        date.range.end
      );
      otherData = filterSortMapSellsByDay(
        orderPaymentDailySummary.filter(
          (v) => v._id.code != "card" && v._id.code != "cash"
        ),
        date.range.start,
        date.range.end
      );

      if (date.periodType === "month") {
        cardData = cardData.map((data, i) => ({
          ...data,
          label: i + 1,
        }));
        cashData = cashData.map((data, i) => ({
          ...data,
          label: i + 1,
        }));
        otherData = otherData.map((data, i) => ({
          ...data,
          label: i + 1,
        }));
      }
    } else {
      cardData = filterSortMapSellsByMonth(
        orderPaymentMonthlySummary.filter((v) => v._id.code === "card"),
        date.range.start,
        date.range.end
      );
      cashData = filterSortMapSellsByMonth(
        orderPaymentMonthlySummary.filter((v) => v._id.code === "cash"),
        date.range.start,
        date.range.end
      );
      otherData = filterSortMapSellsByMonth(
        orderPaymentMonthlySummary.filter(
          (v) => v._id.code != "card" && v._id.code != "cash"
        ),
        date.range.start,
        date.range.end
      );
    }
    return {
      barChartTitle,
      cardData,
      cashData,
      otherData,
    };
  }, [
    date.periodType,
    date.range.end,
    date.range.start,
    isPeriodDay,
    isPeriodWeekOrMonth,
    isPeriodYear,
    orderPaymentDailySummary,
    orderPaymentHourlySummary,
    orderPaymentMonthlySummary,
  ]);

  const loading = loading1 || loading2 || loading3 || loading4 || loading5;

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t("containers.ReportsPaymentMethods.report")}${getDateHeader(
          date.range.start,
          date.range.end
        )}`,
      ],
      [],
      [`${t("containers.ReportsPaymentMethods.paymentMethods")}`],
      [
        `${t("containers.ReportsPaymentMethods.method")}`,
        `${t("containers.ReportsPaymentMethods.sells")}`,
        `${t("containers.ReportsPaymentMethods.returns")}`,
        `${t("containers.ReportsPaymentMethods.operations")}`,
        `${t("containers.ReportsPaymentMethods.earnings")}`,
      ],
    ];

    paymentMethods.forEach((item) => {
      worksheetData.push([
        item.method,
        item.sells,
        item.returns,
        item.operations,
        item.earnings,
      ]);
    });

    const workbook = utils.book_new();
    const sheetName = `${t("containers.ReportsPaymentMethods.paymentMethods")}`;
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(
      workbook,
      `${t("containers.ReportsPaymentMethods.paymentMethods")}.xlsx`
    );
  }, [date.range.end, date.range.start, paymentMethods]);

  const getImageGraph = useCallback(async () => {
    const img1 = await toPng(firstGraph.current, { cacheBust: true });

    const img2 = await toPng(secondGraph.current, { cacheBust: true });

    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end)[0],
          tableValues,
          img1,
          img2,
        }}
      />,
      `${t("containers.ReportsPaymentMethods.paymentMethods")}.pdf`
    );
  }, [date.range.end, date.range.start, tableValues]);

  return (
    <s.PageContainer>
      <TopMenu
        loading={loading}
        value={date.value}
        onChange={(value, periodType, range) => {
          setDate({ value, periodType, range });
        }}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={orderPaymentSummary.length === 0}
      />

      <s.ChartComponentsWrapper>
        <StackedBarChart
          tableValues={tableValues}
          noData={tableValues.length === 0}
          firstGraph={firstGraph}
          loading={loading}
        />
        <s.barChartContainer>
          <BarChart
            title={barChartTitle}
            cardData={cardData}
            cashData={cashData}
            otherData={otherData}
            loading={loading}
            noData={tableValues.length === 0}
            secondGraph={secondGraph}
          />
        </s.barChartContainer>
      </s.ChartComponentsWrapper>
      <List
        width="700px"
        items={tableValues}
        title={t("containers.ReportsPaymentMethods.paymentMethods")}
        columns={[
          {
            title: t("containers.ReportsPaymentMethods.method"),
          },
          {
            title: t("containers.ReportsPaymentMethods.sells"),
          },
          {
            title: t("containers.ReportsPaymentMethods.returns"),
          },
          {
            title: t("containers.ReportsPaymentMethods.operations"),
          },
          {
            title: t("containers.ReportsPaymentMethods.earnings"),
            sortable: true,
            columnId: "earnings",
          },
        ]}
        tableStyle={{ gridTemplateColumns: "26fr 21fr 19fr 17fr 17fr" }}
        sorting={sorting}
        changeSorting={(columnId, order) => setSorting({ columnId, order })}
        loading={loading}
      />
    </s.PageContainer>
  );
}

export default ReportsPaymentMethods;
